import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    IconButton,
    Input,
    Link,
    Sheet,
    Table,
    Textarea,
    Typography
} from '@mui/joy';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import axiosConfig from '../util/axiosConfig';
import { formatDate, formatDateTime } from '../util/dateFormat';

const emptyLink = { label: '', url: '' };

const statusConfig = {
    assigned: { label: 'Assigned', color: 'primary' },
    submitted: { label: 'Submitted', color: 'warning' },
    under_review: { label: 'Under Review', color: 'warning' },
    viva_scheduled: { label: 'Viva Scheduled', color: 'neutral' },
    completed: { label: 'Completed', color: 'success' },
    overdue: { label: 'Overdue', color: 'danger' }
};

const projectTypeLabels = {
    mini: 'Mini Project',
    major: 'Major Project'
};

const getStatusConfig = (status) => statusConfig[status] || { label: status || '-', color: 'neutral' };

const getProjectFileBaseUrl = () => {
    if (process.env.REACT_APP_NODE_ENV === 'development') {
        return 'http://localhost:5090';
    } else if (process.env.REACT_APP_NODE_ENV === 'staging') {
        return 'https://stage.vectorindia.org';
    }

    return 'https://office.vectorindia.org';
};

const getAttachmentHref = (path) => {
    if (!path) return '';
    const pathValue = String(path);
    if (pathValue.startsWith('http://') || pathValue.startsWith('https://')) return pathValue;

    const normalizedPath = pathValue.startsWith('/') ? pathValue : `/${pathValue}`;
    return `${getProjectFileBaseUrl()}${normalizedPath}`;
};

const parseJsonValue = (value) => {
    if (typeof value !== 'string') return value;

    try {
        return JSON.parse(value);
    } catch (err) {
        return value;
    }
};

const splitAttachmentNames = (name) => (
    typeof name === 'string'
        ? name.split(',').map(item => item.trim()).filter(Boolean)
        : []
);

const normalizeAttachmentItem = (item, index, fallbackNames = []) => {
    if (!item) return null;

    if (typeof item === 'string') {
        return {
            path: item,
            name: fallbackNames[index] || `Project attachment ${index + 1}`
        };
    }

    const path = item.path || item.url || item.href;
    if (!path) return null;

    return {
        path,
        name: item.name || item.fileName || fallbackNames[index] || `Project attachment ${index + 1}`
    };
};

const parseProjectAttachments = (attachment) => {
    if (!attachment) return [];

    const attachmentData = parseJsonValue(attachment);
    const source = typeof attachmentData === 'string' ? { path: attachmentData } : attachmentData;
    const fallbackNames = splitAttachmentNames(source.name);
    const parsedPath = parseJsonValue(source.path);

    if (Array.isArray(parsedPath)) {
        return parsedPath
            .map((item, index) => normalizeAttachmentItem(item, index, fallbackNames))
            .filter(Boolean);
    }

    if (parsedPath && typeof parsedPath === 'object') {
        const nestedPath = parseJsonValue(parsedPath.path);

        if (Array.isArray(nestedPath)) {
            return nestedPath
                .map((item, index) => normalizeAttachmentItem(item, index, fallbackNames))
                .filter(Boolean);
        }

        const normalizedItem = normalizeAttachmentItem(parsedPath, 0, fallbackNames);
        return normalizedItem ? [normalizedItem] : [];
    }

    const normalizedItem = normalizeAttachmentItem({ path: source.path, name: source.name }, 0, fallbackNames);
    return normalizedItem ? [normalizedItem] : [];
};

const getKolkataDayStamp = (dateValue) => {
    if (!dateValue) return null;

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;

    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).formatToParts(date);

    const values = parts.reduce((acc, part) => {
        if (part.type !== 'literal') acc[part.type] = Number(part.value);
        return acc;
    }, {});

    return Date.UTC(values.year, values.month - 1, values.day);
};

const getDaysLeftMeta = (deadlineDate, status) => {
    if (status === 'completed') {
        return { label: 'Completed', color: 'success' };
    }

    const deadlineStamp = getKolkataDayStamp(deadlineDate);
    const todayStamp = getKolkataDayStamp(new Date());
    if (!deadlineStamp || !todayStamp) {
        return { label: 'No deadline', color: 'neutral' };
    }

    const daysLeft = Math.round((deadlineStamp - todayStamp) / (24 * 60 * 60 * 1000));

    if (daysLeft === 0) return { label: 'Due today', color: 'warning' };
    if (daysLeft > 0) {
        return {
            label: `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`,
            color: daysLeft <= 3 ? 'warning' : 'success'
        };
    }

    const overdueDays = Math.abs(daysLeft);
    return {
        label: `${overdueDays} ${overdueDays === 1 ? 'day' : 'days'} overdue`,
        color: 'danger'
    };
};

const parseReviewComments = (reviewNotes) => {
    if (!reviewNotes) return [];

    try {
        const parsed = typeof reviewNotes === 'string' ? JSON.parse(reviewNotes) : reviewNotes;
        if (parsed && Array.isArray(parsed.comments)) {
            return parsed.comments
                .filter(comment => comment && comment.message)
                .map(comment => ({
                    message: comment.message,
                    timestamp: comment.timestamp || null
                }));
        }
    } catch (err) {
        // Fallback below keeps older plain-text notes readable.
    }

    return [{ message: reviewNotes, timestamp: null }];
};

const normalizeProjectUrl = (url) => {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;

    return `https://${trimmed}`;
};

const isValidProjectUrl = (url) => {
    try {
        const parsedUrl = new URL(url);
        return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch (err) {
        return false;
    }
};

const projectMetaChipsSx = {
    display: 'flex',
    gap: 1,
    flexWrap: 'wrap',
    mt: 1,
    alignItems: 'center'
};

const tableSx = {
    '& tbody tr:nth-of-type(even)': {
        backgroundColor: '#f9f9f9'
    },
    '& tbody tr:hover': {
        backgroundColor: '#fff5ed'
    },
    '& thead': {
        position: 'sticky',
        top: 0,
        zIndex: 10
    },
    '& thead th': {
        backgroundColor: '#eb7a24',
        color: 'white',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: '2px solid #e55125'
    },
    '& td, & th': {
        padding: '12px',
        border: '1px solid',
        borderColor: 'divider'
    },
    '& td': {
        verticalAlign: 'top'
    }
};

const assignedProjectsColumnWidths = {
    project: 220,
    deadline: 160,
    status: 110,
    primary: 75
};

const getAssignedProjectsMinTableWidth = (includePrimaryColumn) => (
    includePrimaryColumn
        ? assignedProjectsColumnWidths.project
            + assignedProjectsColumnWidths.deadline
            + assignedProjectsColumnWidths.status
            + assignedProjectsColumnWidths.primary
        : assignedProjectsColumnWidths.project
            + assignedProjectsColumnWidths.deadline
            + assignedProjectsColumnWidths.status
);

const getAssignedProjectsTableSx = (includePrimaryColumn) => ({
    ...tableSx,
    width: '100%',
    minWidth: getAssignedProjectsMinTableWidth(includePrimaryColumn),
    tableLayout: 'fixed',
    '& th, & td': {
        whiteSpace: 'normal',
        overflow: 'hidden',
        wordBreak: 'break-word'
    },
    '& th:nth-of-type(1), & td:nth-of-type(1)': {
        width: includePrimaryColumn ? '46%' : '52%',
        minWidth: assignedProjectsColumnWidths.project
    },
    '& th:nth-of-type(2), & td:nth-of-type(2)': {
        width: includePrimaryColumn ? '28%' : '30%',
        minWidth: assignedProjectsColumnWidths.deadline
    },
    '& th:nth-of-type(3), & td:nth-of-type(3)': {
        width: includePrimaryColumn ? '18%' : '18%',
        minWidth: assignedProjectsColumnWidths.status
    },
    ...(includePrimaryColumn ? {
        '& th:nth-of-type(4), & td:nth-of-type(4)': {
            width: '8%',
            minWidth: assignedProjectsColumnWidths.primary,
            textAlign: 'center'
        }
    } : {})
});

const focusSx = {
    '&:hover': { borderColor: '#ea7a23' },
    '&:focus-within': { borderColor: '#ea7a23' }
};

const Projects = ({ onProjectMessagesRead }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { assignmentId } = useParams();
    const chatSectionRef = useRef(null);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [submissionLinks, setSubmissionLinks] = useState([emptyLink]);
    const [submissionLinkErrors, setSubmissionLinkErrors] = useState({});
    const [submissionNotes, setSubmissionNotes] = useState('');
    const [isEditingSubmission, setIsEditingSubmission] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [messages, setMessages] = useState([]);
    const [chatMessage, setChatMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [markingAllRead, setMarkingAllRead] = useState(false);
    const [markingMessageId, setMarkingMessageId] = useState(null);
    const [lastMessagesUpdatedAt, setLastMessagesUpdatedAt] = useState(null);
    const [settingPrimaryId, setSettingPrimaryId] = useState(null);

    const fetchProjects = useCallback(async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/projects');
            setProjects(response.data.projects || []);
        } catch (err) {
            console.error('Error fetching projects:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load projects');
        } finally {
            setLoadingProjects(false);
        }
    }, []);

    const fetchProjectDetail = useCallback(async (id) => {
        try {
            setLoadingDetail(true);
            setError(null);
            const response = await axiosConfig.get(`/students/projects/${id}`);
            const project = response.data.project;
            setSelectedProject(project);
            setSubmissionLinks(project?.submission?.links?.length ? project.submission.links : [emptyLink]);
            setSubmissionLinkErrors({});
            setSubmissionNotes(project?.submissionNotes || '');
            setIsEditingSubmission(false);
        } catch (err) {
            console.error('Error fetching project details:', err);
            setSelectedProject(null);
            setError(err.response?.data?.errorMessage || 'Failed to load project details');
        } finally {
            setLoadingDetail(false);
        }
    }, []);

    const fetchMessages = useCallback(async (id, showLoader = false) => {
        try {
            if (showLoader) setLoadingMessages(true);
            const response = await axiosConfig.get(`/students/projects/${id}/messages`);
            setMessages(response.data.messages || []);
            setLastMessagesUpdatedAt(new Date());
            if (typeof onProjectMessagesRead === 'function') {
                onProjectMessagesRead({ showNotification: false });
            }
        } catch (err) {
            console.error('Error fetching project messages:', err);
        } finally {
            if (showLoader) setLoadingMessages(false);
        }
    }, [onProjectMessagesRead]);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    useEffect(() => {
        if (!assignmentId) {
            setSelectedProject(null);
            setMessages([]);
            setLastMessagesUpdatedAt(null);
            return undefined;
        }

        fetchProjectDetail(assignmentId);
        fetchMessages(assignmentId, true);
        return undefined;
    }, [assignmentId, fetchProjectDetail, fetchMessages]);

    useEffect(() => {
        const shouldFocusChat = new URLSearchParams(location.search).get('focus') === 'chat';
        if (!selectedProject || !shouldFocusChat) return;

        window.setTimeout(() => {
            chatSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    }, [location.search, selectedProject, messages.length]);

    const openProject = (id) => {
        setSuccess(null);
        navigate(`/projects/${id}`);
    };

    const setPrimaryProject = async (project) => {
        if (!project || project.isPrimaryProject) return;

        try {
            setSettingPrimaryId(project.assignmentId);
            setError(null);
            const response = await axiosConfig.patch(`/students/projects/${project.assignmentId}/primary`);
            await fetchProjects();

            if (selectedProject?.assignmentId === project.assignmentId) {
                setSelectedProject(response.data.project);
            }

            setSuccess(response.data.message || 'Primary project updated successfully.');
        } catch (err) {
            console.error('Error setting primary project:', err);
            setError(err.response?.data?.errorMessage || 'Failed to set primary project');
        } finally {
            setSettingPrimaryId(null);
        }
    };

    const updateSubmissionLink = (index, field, value) => {
        setSubmissionLinks((currentLinks) => currentLinks.map((link, linkIndex) => (
            linkIndex === index ? { ...link, [field]: value } : link
        )));
        if (field === 'url') {
            setSubmissionLinkErrors((currentErrors) => {
                const nextErrors = { ...currentErrors };
                delete nextErrors[index];
                return nextErrors;
            });
        }
    };

    const handleSubmissionUrlBlur = (index) => {
        const currentUrl = submissionLinks[index]?.url || '';
        const normalizedUrl = normalizeProjectUrl(currentUrl);

        if (normalizedUrl !== currentUrl) {
            updateSubmissionLink(index, 'url', normalizedUrl);
        }
    };

    const addSubmissionLink = () => {
        setSubmissionLinks((currentLinks) => [...currentLinks, { ...emptyLink }]);
    };

    const removeSubmissionLink = (index) => {
        setSubmissionLinks((currentLinks) => {
            const nextLinks = currentLinks.filter((_, linkIndex) => linkIndex !== index);
            return nextLinks.length ? nextLinks : [{ ...emptyLink }];
        });
        setSubmissionLinkErrors((currentErrors) => {
            const nextErrors = {};
            Object.entries(currentErrors).forEach(([errorIndex, errorMessage]) => {
                const numericIndex = Number(errorIndex);
                if (numericIndex < index) nextErrors[numericIndex] = errorMessage;
                if (numericIndex > index) nextErrors[numericIndex - 1] = errorMessage;
            });
            return nextErrors;
        });
    };

    const submitProject = async () => {
        if (!selectedProject) return;

        setError(null);
        setSuccess(null);

        const links = submissionLinks
            .map(link => ({
                label: link.label.trim(),
                url: normalizeProjectUrl(link.url)
            }))
            .filter(link => link.url);

        if (!links.length) {
            setSubmissionLinkErrors({ 0: 'Please enter at least one project URL.' });
            return;
        }

        const nextLinkErrors = {};
        submissionLinks.forEach((link, index) => {
            const url = normalizeProjectUrl(link.url);
            if (url && !isValidProjectUrl(url)) {
                nextLinkErrors[index] = 'Please enter a valid http or https URL.';
            }
        });

        if (Object.keys(nextLinkErrors).length > 0) {
            setSubmissionLinkErrors(nextLinkErrors);
            return;
        }

        setSubmissionLinks((currentLinks) => currentLinks.map(link => ({
            ...link,
            url: link.url.trim() ? normalizeProjectUrl(link.url) : link.url
        })));

        try {
            setSubmitting(true);
            setError(null);
            setSuccess(null);
            setSubmissionLinkErrors({});
            const response = await axiosConfig.put(`/students/projects/${selectedProject.assignmentId}/submission`, {
                links,
                notes: submissionNotes
            });
            setSelectedProject(response.data.project);
            setIsEditingSubmission(false);
            setSuccess(response.data.message || 'Project submitted successfully.');
            await fetchProjects();
        } catch (err) {
            console.error('Error submitting project:', err);
            const errorMessage = err.response?.data?.errorMessage || 'Failed to submit project';
            if (errorMessage.toLowerCase().includes('valid http')) {
                const firstUrlIndex = Math.max(0, submissionLinks.findIndex(link => link.url.trim()));
                setSubmissionLinkErrors({ [firstUrlIndex]: 'Please enter a valid http or https URL.' });
            } else {
                setError(errorMessage);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const sendMessage = async () => {
        if (!selectedProject || !chatMessage.trim()) return;

        try {
            setSendingMessage(true);
            setError(null);
            await axiosConfig.post(`/students/projects/${selectedProject.assignmentId}/messages`, {
                message: chatMessage.trim()
            });
            setChatMessage('');
            await fetchMessages(selectedProject.assignmentId);
        } catch (err) {
            console.error('Error sending project message:', err);
            setError(err.response?.data?.errorMessage || 'Failed to send message');
        } finally {
            setSendingMessage(false);
        }
    };

    const markMessageAsRead = async (message) => {
        if (!selectedProject || message.senderType !== 'academic' || message.isRead) return;

        try {
            setMarkingMessageId(message.id);
            await axiosConfig.patch(`/students/projects/${selectedProject.assignmentId}/messages/message/${message.id}/read`);
            setMessages((currentMessages) => currentMessages.map(currentMessage => (
                currentMessage.id === message.id ? { ...currentMessage, isRead: true } : currentMessage
            )));
            if (typeof onProjectMessagesRead === 'function') {
                onProjectMessagesRead({ showNotification: false });
            }
        } catch (err) {
            console.error('Error marking project message as read:', err);
            setError(err.response?.data?.errorMessage || 'Failed to mark message as read');
        } finally {
            setMarkingMessageId(null);
        }
    };

    const markAllMessagesAsRead = async () => {
        if (!selectedProject) return;

        try {
            setMarkingAllRead(true);
            await axiosConfig.patch(`/students/projects/${selectedProject.assignmentId}/messages/read`);
            setMessages((currentMessages) => currentMessages.map(currentMessage => (
                currentMessage.senderType === 'academic' ? { ...currentMessage, isRead: true } : currentMessage
            )));
            if (typeof onProjectMessagesRead === 'function') {
                onProjectMessagesRead({ showNotification: false });
            }
        } catch (err) {
            console.error('Error marking project messages as read:', err);
            setError(err.response?.data?.errorMessage || 'Failed to mark messages as read');
        } finally {
            setMarkingAllRead(false);
        }
    };

    const renderStatusChip = (status) => {
        if (status === 'completed') {
            return (
                <img
                    src="/completed_stamp.png"
                    alt="Completed"
                    style={{
                        height: '90px',
                        width: 'auto',
                        objectFit: 'contain',
                        display: 'inline-block',
                        verticalAlign: 'middle',
                        transform: 'rotate(-4deg)',
                        margin: '2px 0'
                    }}
                />
            );
        }
        const config = getStatusConfig(status);
        return (
            <Chip color={config.color} variant="soft" size="sm">
                {config.label}
            </Chip>
        );
    };

    const canEditSubmission = selectedProject && ['assigned', 'submitted', 'overdue'].includes(selectedProject.status);
    const showSubmissionForm = selectedProject && (!selectedProject.submittedAt || isEditingSubmission);
    const canSubmit = canEditSubmission && showSubmissionForm;
    const unreadAcademicMessageCount = messages.filter(message => message.senderType === 'academic' && !message.isRead).length;
    const projectAttachments = parseProjectAttachments(selectedProject?.attachment);
    const majorCount = projects.filter(p => p.projectType?.toLowerCase() === 'major').length;
    const hasMajorProjects = majorCount > 0;
    const showPrimaryProjectOption = true;

    if (loadingProjects) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: 2 }}>
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading projects...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: '1500px', mx: 'auto', width: '100%', minWidth: 0 }}>
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <AssignmentIcon sx={{ color: '#eb7a24', fontSize: '1.7rem' }} />
                        <Typography level="h3" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                            Projects
                        </Typography>
                        <Chip variant="soft" color="neutral">{projects.length} assigned</Chip>
                    </Box>
                    <Typography level="body-sm" sx={{ mt: 1, color: '#606162' }}>
                        View your assigned projects, submit project links, and chat with the academic team about each project.
                    </Typography>
                </CardContent>
            </Card>

            {error && (
                <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert color="success" variant="soft" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    minWidth: 0
                }}
            >
                <Card variant="outlined" sx={{ minWidth: 0, overflow: 'hidden' }}>
                    <CardContent sx={{ minWidth: 0 }}>
                        <Typography level="h4" sx={{ color: '#eb7a24', mb: 2 }}>
                            Assigned Projects
                        </Typography>

                        {projects.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography level="body-lg" sx={{ color: '#6e7072' }}>
                                    No projects are assigned to you yet.
                                </Typography>
                            </Box>
                        ) : (
                            <Sheet
                                variant="outlined"
                                sx={{
                                    borderRadius: 'sm',
                                    width: '100%',
                                    maxWidth: '100%',
                                    overflowX: 'auto',
                                    overflowY: 'auto',
                                    maxHeight: '620px',
                                    WebkitOverflowScrolling: 'touch',
                                    display: 'block'
                                }}
                            >
                                <Table
                                    aria-label="assigned projects table"
                                    sx={getAssignedProjectsTableSx(hasMajorProjects)}
                                >
                                    <thead>
                                        <tr>
                                            <th>Project</th>
                                            <th>Deadline / Days Left</th>
                                            <th>Status</th>
                                            {hasMajorProjects && (
                                                <th style={{ textAlign: 'center' }}>Primary</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {projects.map(project => (
                                            <tr
                                                key={project.assignmentId}
                                                tabIndex={0}
                                                role="button"
                                                onClick={() => openProject(project.assignmentId)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        openProject(project.assignmentId);
                                                    }
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td>
                                                    <Typography level="title-sm">{project.title}</Typography>
                                                    <Typography level="body-xs" sx={{ color: '#6e7072' }}>
                                                        {projectTypeLabels[project.projectType] || project.projectType}
                                                    </Typography>
                                                    <Typography level="body-xs" sx={{ color: '#6e7072' }}>
                                                        Assigned: {formatDate(project.assignedAt)}
                                                    </Typography>
                                                </td>
                                                <td>
                                                    <DeadlineDisplay deadlineDate={project.deadlineDate} status={project.status} />
                                                </td>
                                                <td>{renderStatusChip(project.status)}</td>
                                                {hasMajorProjects && (
                                                    <td style={{ textAlign: 'center' }}>
                                                        {project.projectType?.toLowerCase() === 'major' && (
                                                            project.isPrimaryProject ? (
                                                                <Chip size="sm" color="warning" variant="soft" startDecorator={<StarIcon sx={{ fontSize: '0.9rem' }} />}>
                                                                    Primary
                                                                </Chip>
                                                            ) : (
                                                                showPrimaryProjectOption ? (
                                                                    <IconButton
                                                                        size="sm"
                                                                        variant="soft"
                                                                        color="warning"
                                                                        aria-label="Set as primary project"
                                                                        title="Set as primary project"
                                                                        loading={settingPrimaryId === project.assignmentId}
                                                                        disabled={Boolean(settingPrimaryId)}
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            setPrimaryProject(project);
                                                                        }}
                                                                    >
                                                                        <StarBorderIcon />
                                                                    </IconButton>
                                                                ) : null
                                                            )
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Sheet>
                        )}
                    </CardContent>
                </Card>

                {(assignmentId || selectedProject) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {loadingDetail ? (
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '250px', gap: 2 }}>
                                        <CircularProgress />
                                        <Typography>Loading project details...</Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        ) : selectedProject ? (
                            <>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                                            <Box>
                                                <Typography level="h4" sx={{ color: '#eb7a24' }}>
                                                    {selectedProject.title}
                                                </Typography>
                                                <Box sx={projectMetaChipsSx}>
                                                    {renderStatusChip(selectedProject.status)}
                                                    <Chip size="sm" variant="soft" color="neutral" sx={{ alignSelf: 'center' }}>
                                                        {projectTypeLabels[selectedProject.projectType] || selectedProject.projectType}
                                                    </Chip>
                                                    {selectedProject.projectType?.toLowerCase() === 'major' && selectedProject.isPrimaryProject && (
                                                        <Chip
                                                            size="sm"
                                                            color="warning"
                                                            variant="solid"
                                                            sx={{ alignSelf: 'center' }}
                                                            startDecorator={<StarIcon sx={{ fontSize: '0.9rem' }} />}
                                                        >
                                                            Primary Project
                                                        </Chip>
                                                    )}
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                                {showPrimaryProjectOption && selectedProject.projectType?.toLowerCase() === 'major' && !selectedProject.isPrimaryProject && (
                                                    <IconButton
                                                        size="sm"
                                                        variant="soft"
                                                        color="warning"
                                                        aria-label="Set as primary project"
                                                        title="Set as primary project"
                                                        loading={settingPrimaryId === selectedProject.assignmentId}
                                                        disabled={Boolean(settingPrimaryId)}
                                                        onClick={() => setPrimaryProject(selectedProject)}
                                                    >
                                                        <StarBorderIcon />
                                                    </IconButton>
                                                )}
                                                <Button variant="plain" color="neutral" onClick={() => navigate('/projects')}>
                                                    Back to List
                                                </Button>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
                                            <InfoBlock label="Start Date" value={formatDate(selectedProject.startDate)} />
                                            <InfoBlock
                                                label="Deadline / Days Left"
                                                value={<DeadlineDisplay deadlineDate={selectedProject.deadlineDate} status={selectedProject.status} />}
                                            />
                                            <InfoBlock label="Submitted" value={formatDateTime(selectedProject.submittedAt)} />
                                        </Box>

                                        <Typography level="title-md" sx={{ mb: 1 }}>Project Details</Typography>
                                        <Typography level="body-md" sx={{ whiteSpace: 'pre-wrap', color: '#404041' }}>
                                            {selectedProject.description || 'No description provided.'}
                                        </Typography>

                                        {projectAttachments.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography level="title-sm" sx={{ mb: 0.75, color: '#eb7a24' }}>
                                                    Project Attachments
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
                                                    {projectAttachments.map((attachment, index) => (
                                                        <Link
                                                            key={`${attachment.path}-${index}`}
                                                            href={getAttachmentHref(attachment.path)}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            startDecorator={<OpenInNewIcon fontSize="small" />}
                                                        >
                                                            {attachment.name}
                                                        </Link>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>

                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography level="h4" sx={{ color: '#eb7a24', mb: 1 }}>
                                            Project Submission Guidelines
                                        </Typography>
                                        <Box
                                            component="ol"
                                            sx={{
                                                pl: 3,
                                                m: 0,
                                                color: '#404041',
                                                '& > li': { mb: 1 },
                                                '& li': { lineHeight: 1.6 }
                                            }}
                                        >
                                            <li>Each student will be assigned a project along with the project duration/submission date.</li>
                                            <li>Students must complete the project implementation on real hardware within the given time frame.</li>
                                            <li>
                                                After successful implementation, students must prepare a proper GitHub repository containing:
                                                <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
                                                    <li>Source code</li>
                                                    <li>Circuit details</li>
                                                    <li>Images/videos (if required)</li>
                                                    <li>README file and project details</li>
                                                </Box>
                                            </li>
                                            <li>Submit the GitHub repository link through the student login portal.</li>
                                            <li>After submission, the project will enter the review process.</li>
                                            <li>If any modifications or corrections are required, students will receive notifications/messages in their login portal.</li>
                                            <li>Students must complete the project explanation and viva within the assigned project duration.</li>
                                            <li>
                                                After successful completion of:
                                                <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
                                                    <li>Hardware verification</li>
                                                    <li>GitHub review</li>
                                                    <li>Explanation & viva</li>
                                                    <li>Final evaluation</li>
                                                    <li>Project completion</li>
                                                </Box>
                                            </li>
                                            <li>Entire process must be completed within the assigned project duration.
                                            </li>
                                        </Box>
                                    </CardContent>
                                </Card>

                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography level="h4" sx={{ color: '#eb7a24', mb: 2 }}>
                                            Submit Project
                                        </Typography>

                                        {!canEditSubmission ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                <Typography level="body-md" sx={{ color: '#404041' }}>
                                                    Current project status
                                                </Typography>
                                                <Box>{renderStatusChip(selectedProject.status)}</Box>
                                                {selectedProject.deadlineDate && (
                                                    <Typography level="body-md" sx={{ color: '#404041' }}>
                                                        Submission date: {formatDate(selectedProject.deadlineDate)}
                                                    </Typography>
                                                )}
                                                {selectedProject.submittedAt && (
                                                    <Typography level="body-sm" sx={{ color: '#6e7072' }}>
                                                        Submitted on {formatDateTime(selectedProject.submittedAt)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : selectedProject.submittedAt && !showSubmissionForm ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                <Alert color="neutral" variant="soft">
                                                    Submitted on {formatDateTime(selectedProject.submittedAt)}
                                                </Alert>
                                                <Button
                                                    variant="soft"
                                                    color="warning"
                                                    onClick={() => setIsEditingSubmission(true)}
                                                    sx={{ alignSelf: 'flex-start' }}
                                                >
                                                    Edit Submission
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                {selectedProject.submittedAt && (
                                                    <Alert color="neutral" variant="soft">
                                                        Submitted on {formatDateTime(selectedProject.submittedAt)}
                                                    </Alert>
                                                )}

                                                {submissionLinks.map((link, index) => (
                                                    <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '180px 1fr auto' }, gap: 1, alignItems: 'end' }}>
                                                        <FormControl>
                                                            <FormLabel>Label</FormLabel>
                                                            <Input
                                                                value={link.label}
                                                                onChange={(event) => updateSubmissionLink(index, 'label', event.target.value)}
                                                                placeholder="GitHub, Demo, Docs"
                                                                disabled={!canSubmit}
                                                                sx={focusSx}
                                                            />
                                                        </FormControl>
                                                        <FormControl error={Boolean(submissionLinkErrors[index])}>
                                                            <FormLabel>Project URL</FormLabel>
                                                            <Input
                                                                value={link.url}
                                                                onChange={(event) => updateSubmissionLink(index, 'url', event.target.value)}
                                                                onBlur={() => handleSubmissionUrlBlur(index)}
                                                                placeholder="https://..."
                                                                disabled={!canSubmit}
                                                                sx={focusSx}
                                                            />
                                                            {submissionLinkErrors[index] && (
                                                                <FormHelperText>
                                                                    {submissionLinkErrors[index]}
                                                                </FormHelperText>
                                                            )}
                                                        </FormControl>
                                                        <IconButton
                                                            color="danger"
                                                            variant="soft"
                                                            disabled={!canSubmit || submissionLinks.length === 1}
                                                            onClick={() => removeSubmissionLink(index)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                ))}

                                                <Button
                                                    variant="soft"
                                                    color="warning"
                                                    startDecorator={<AddIcon />}
                                                    onClick={addSubmissionLink}
                                                    disabled={!canSubmit || submissionLinks.length >= 10}
                                                    sx={{ alignSelf: 'flex-start' }}
                                                >
                                                    Add URL
                                                </Button>

                                                <FormControl>
                                                    <FormLabel>Submission Notes</FormLabel>
                                                    <Textarea
                                                        minRows={4}
                                                        value={submissionNotes}
                                                        onChange={(event) => setSubmissionNotes(event.target.value)}
                                                        placeholder="Add setup steps, completed modules, credentials, or reviewer notes..."
                                                        disabled={!canSubmit}
                                                        sx={focusSx}
                                                    />
                                                </FormControl>

                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Button
                                                        loading={submitting}
                                                        disabled={!canSubmit || submitting}
                                                        onClick={submitProject}
                                                        sx={{ backgroundColor: '#eb7a24', '&:hover': { backgroundColor: '#e55125' }, maxWidth: '220px' }}
                                                    >
                                                        {selectedProject.submittedAt ? 'Resubmit Project' : 'Submit Project'}
                                                    </Button>
                                                    {selectedProject.submittedAt && (
                                                        <Button
                                                            variant="plain"
                                                            color="neutral"
                                                            disabled={submitting}
                                                            onClick={() => setIsEditingSubmission(false)}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>

                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography level="h4" sx={{ color: '#eb7a24', mb: 2 }}>
                                            Result
                                        </Typography>
                                        {(selectedProject.status === 'completed' || selectedProject.reviewNotes || selectedProject.vivaNotes) && (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                {selectedProject.status === 'completed' && (
                                                    <Alert color="success" variant="soft">
                                                        Completed Successfully
                                                    </Alert>
                                                )}
                                                {(selectedProject.reviewNotes || selectedProject.vivaNotes) && (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                        <ReviewNotesBlock label="Review Notes" value={selectedProject.reviewNotes} />
                                                        <ReviewNotesBlock label="Viva Notes" value={selectedProject.vivaNotes} />
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>

                                <Card variant="outlined" ref={chatSectionRef}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography level="h4" sx={{ color: '#eb7a24' }}>
                                                    Project Chat
                                                </Typography>
                                                <Typography level="body-xs" sx={{ color: '#6e7072' }}>
                                                    Last updated: {lastMessagesUpdatedAt ? formatDateTime(lastMessagesUpdatedAt) : 'Not updated yet'}
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="soft"
                                                color="warning"
                                                loading={markingAllRead}
                                                disabled={unreadAcademicMessageCount === 0 || markingAllRead || loadingMessages}
                                                onClick={markAllMessagesAsRead}
                                            >
                                                Mark all as read
                                            </Button>
                                        </Box>

                                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', minHeight: '240px', maxHeight: '420px', overflow: 'auto', backgroundColor: '#fafafa' }}>
                                            {messages.length === 0 ? (
                                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                                    <Typography level="body-md" sx={{ color: '#6e7072' }}>
                                                        No chat messages yet. Start a conversation with the academic team.
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    {messages.map(message => {
                                                        const isStudent = message.senderType === 'student';
                                                        const isUnreadAcademic = !isStudent && !message.isRead;
                                                        return (
                                                            <Box key={message.id} sx={{ display: 'flex', justifyContent: isStudent ? 'flex-end' : 'flex-start' }}>
                                                                <Box
                                                                    onClick={() => markMessageAsRead(message)}
                                                                    sx={{
                                                                        maxWidth: '78%',
                                                                        p: 1.5,
                                                                        borderRadius: '12px',
                                                                        backgroundColor: isStudent ? '#fff0e6' : 'white',
                                                                        border: '1px solid',
                                                                        borderColor: isUnreadAcademic ? '#eb7a24' : (isStudent ? '#f19b30' : 'divider'),
                                                                        cursor: isUnreadAcademic ? 'pointer' : 'default',
                                                                        boxShadow: isUnreadAcademic ? 'sm' : 'none'
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                        <Typography level="title-sm" sx={{ color: isStudent ? '#e55125' : '#404041' }}>
                                                                            {isStudent ? 'You' : (message.senderName || 'Academic Team')}
                                                                        </Typography>
                                                                        {isUnreadAcademic && (
                                                                            <Chip color="warning" variant="soft" size="sm">
                                                                                {markingMessageId === message.id ? 'Marking...' : 'Unread'}
                                                                            </Chip>
                                                                        )}
                                                                    </Box>
                                                                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                                                                        {message.message}
                                                                    </Typography>
                                                                    <Typography level="body-xs" sx={{ mt: 0.75, color: '#6e7072' }}>
                                                                        {formatDateTime(message.createdAt)}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        </Sheet>

                                        <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'flex-end' }}>
                                            <Textarea
                                                value={chatMessage}
                                                onChange={(event) => setChatMessage(event.target.value)}
                                                placeholder="Type your message to the academic team..."
                                                minRows={2}
                                                maxRows={5}
                                                sx={{ ...focusSx, flex: 1 }}
                                            />
                                            <Button
                                                loading={sendingMessage}
                                                disabled={!chatMessage.trim() || sendingMessage}
                                                startDecorator={<SendIcon />}
                                                onClick={sendMessage}
                                                sx={{ backgroundColor: '#eb7a24', '&:hover': { backgroundColor: '#e55125' } }}
                                            >
                                                Send
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </>
                        ) : null}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const InfoBlock = ({ label, value }) => (
    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
        <Typography level="body-xs" sx={{ color: '#6e7072', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {label}
        </Typography>
        <Box sx={{ mt: 0.5, color: '#404041' }}>
            {React.isValidElement(value) ? value : (
                <Typography level="title-sm" sx={{ color: '#404041' }}>
                    {value || '-'}
                </Typography>
            )}
        </Box>
    </Sheet>
);

const ReviewNotesBlock = ({ label = 'Review Notes', value }) => {
    const comments = parseReviewComments(value);

    return (
        <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'sm' }}>
            <Typography level="title-sm" sx={{ color: '#eb7a24', mb: 0.75 }}>
                {label}
            </Typography>
            {comments.length === 0 ? (
                <Typography level="body-sm">-</Typography>
            ) : (
                <Table
                    aria-label="review notes table"
                    sx={{
                        '& td': {
                            p: '8px',
                            border: '1px solid',
                            borderColor: 'divider',
                            verticalAlign: 'top'
                        },
                        '& tbody tr:nth-of-type(even)': {
                            backgroundColor: '#f9f9f9'
                        }
                    }}
                >
                    <tbody>
                        {comments.map((comment, index) => (
                            <tr key={`${comment.timestamp || 'note'}-${index}`}>
                                <td style={{ width: '180px' }}>
                                    <Typography level="body-sm">
                                        {formatDateTime(comment.timestamp)}
                                    </Typography>
                                </td>
                                <td>
                                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {comment.message || '-'}
                                    </Typography>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </Sheet>
    );
};

const DeadlineDisplay = ({ deadlineDate, status }) => {
    const daysLeft = getDaysLeftMeta(deadlineDate, status);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
            <Typography level="body-md">
                {formatDate(deadlineDate)}
            </Typography>
            <Chip color={daysLeft.color} variant="soft" size="sm">
                {daysLeft.label}
            </Chip>
        </Box>
    );
};

export default Projects;
